import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

export interface DependentItem {
  id: string;
  name: string;
  description?: string;
  type?: string;
}

export interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (deleteOrphaned?: boolean) => Promise<void>;
  title: string;
  itemName: string;
  itemType: 'function' | 'object' | 'material' | string;
  message?: string;
  orphanedObject?: {
    id: string;
    name: string;
    description?: string;
  };
  dependentItems?: DependentItem[];
  isDeactivation?: boolean;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType,
  message,
  orphanedObject,
  dependentItems,
  isDeactivation = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteOrphaned, setDeleteOrphaned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actionVerb = isDeactivation ? 'deactivate' : 'delete';
  const actionVerbCapitalized = isDeactivation ? 'Deactivate' : 'Delete';

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await onConfirm(deleteOrphaned);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${actionVerb} ${itemType}`);
    } finally {
      setLoading(false);
    }
  };

  const canDelete = !dependentItems || dependentItems.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        {title || `${actionVerbCapitalized} ${itemType}`}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            {message || (
              <>
                Are you sure you want to {actionVerb} <strong>{itemName}</strong>?
                {isDeactivation && (
                  <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    It will be hidden from lists but preserved for historical data.
                  </Box>
                )}
              </>
            )}
          </Typography>
        </Box>

        {/* Show dependent items if any */}
        {dependentItems && dependentItems.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Cannot {actionVerb} - this {itemType} is in use by:
            </Typography>
            <List dense sx={{ mt: 1 }}>
              {dependentItems.map((item) => (
                <ListItem key={item.id} sx={{ py: 0.5, px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{item.name}</Typography>
                        <Link
                          href={`/dashboard/ai-functions?id=${item.id}`}
                          target="_blank"
                          rel="noopener"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          View
                        </Link>
                      </Box>
                    }
                    secondary={item.description}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Show orphaned object option if applicable */}
        {orphanedObject && canDelete && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Orphaned Object Detected
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The object <strong>{orphanedObject.name}</strong> is only used by this {itemType}.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteOrphaned}
                  onChange={(e) => setDeleteOrphaned(e.target.checked)}
                  color="primary"
                />
              }
              label={`Also ${actionVerb} the orphaned object`}
            />
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={loading || !canDelete}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? `${actionVerbCapitalized}ing...` : `${actionVerbCapitalized} ${itemType}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
